import { api, axiosClient } from '@/lib/api/client';
import type {
  CoContactFilters,
  CoContactListResponse,
  CoContactWithInteractions,
  UpdateCoContactPayload,
  CoContact,
} from '@/lib/api/types';

/**
 * Caso 01 — Leads CRM. The backend exposes /co-contacts under JwtAuthGuard
 * and resolves the caller's billing root via PlanProvisioningService.
 *
 * `list` returns paginated rows; `detail` adds the interaction timeline.
 * `exportCsv` streams text/csv directly; we return the Blob so the caller
 * can wire up a hidden <a download>.
 */
/**
 * `eventIds` viaja como lista separada por comas. Axios serializa un array
 * como `eventIds[]=1&eventIds[]=2`, y el parser de consulta de Express 5 (el
 * de Nest 11) no lo vuelve a juntar: llegaba una clave `eventIds[]` que el DTO
 * ignoraba y el filtro no se aplicaba.
 */
function toParams(filters: CoContactFilters): Record<string, unknown> {
  const { eventIds, ...rest } = filters;
  return eventIds && eventIds.length > 0
    ? { ...rest, eventIds: eventIds.join(',') }
    : rest;
}

export const contactsApi = {
  list: (filters: CoContactFilters) =>
    api.get<CoContactListResponse>('/co-contacts', { params: toParams(filters) }),

  detail: (id: number) =>
    api.get<CoContactWithInteractions>(`/co-contacts/${id}`),

  update: (id: number, payload: UpdateCoContactPayload) =>
    api.patch<CoContact>(`/co-contacts/${id}`, payload),

  remove: (id: number) => api.delete<{ ok: true }>(`/co-contacts/${id}`),

  /**
   * Hits /co-contacts/export with the active filters. The backend streams
   * CSV up to 50k rows and returns 403 over that — caller should narrow
   * the filters in that case.
   */
  exportCsv: async (filters: CoContactFilters, exportAckId: number): Promise<Blob> => {
    const res = await axiosClient.get<Blob>('/co-contacts/export', {
      params: toParams(filters),
      responseType: 'blob',
      // Exhibit F: el Data Export Acknowledgment que ampara ESTA descarga. Sin
      // él el backend responde 428 y no sirve el fichero.
      headers: { 'X-Export-Ack': String(exportAckId) },
    });
    return res.data;
  },
};
